import { SOCIAL_PLATFORMS, buildSocialUrl, normalizeWebsiteUrl } from '@mts/shared/socialLinks';

const text = value => typeof value === 'string' ? value.trim() : '';

export function publicBusinessImageFacts(listing = {}, origin = '') {
  // These fields are displayed by the public hero/photo cards. Gallery entries
  // are URL strings, not raw media/authoring objects.
  const candidates = [listing.imageUrl, ...(Array.isArray(listing.galleryImages) ? listing.galleryImages : []), listing.logoUrl];
  const images = [...new Set(candidates.flatMap(value => {
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
      const url = new URL(value, origin || undefined);
      return url.protocol === 'https:' && !url.username && !url.password ? [url.href] : [];
    } catch { return []; }
  }))];
  return { images: images.length ? images : undefined, imageUrl: images[0] };
}

export function publicSocialProfileLinks(value) {
  const source = Array.isArray(value) ? Object.fromEntries(value.map(entry => [String(entry?.platform || '').toLowerCase(), entry?.url])) : Object.fromEntries(Object.entries(value || {}).map(([key, input]) => [key.toLowerCase(), input]));
  return Object.fromEntries([...SOCIAL_PLATFORMS.map(row => row.platform), 'google'].flatMap(platform => {
    const input = source[platform];
    const url = platform === 'google' ? (/^https?:\/\//i.test(text(input)) ? normalizeWebsiteUrl(input) : '') : buildSocialUrl(platform, input);
    return url ? [[platform, url]] : [];
  }));
}

export function publicBusinessProfileFacts(listing = {}) {
  const social = listing.socialLinks && typeof listing.socialLinks === 'object' && !Array.isArray(listing.socialLinks) ? listing.socialLinks : listing.socialLinksJson;
  const sameAs = [...new Set(Object.values(publicSocialProfileLinks(social)))];
  const weekly = Array.isArray(listing.hours?.weekly) ? listing.hours.weekly : listing.businessHours;
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const time = value => typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value);
  const hours = (Array.isArray(weekly) ? weekly : []).filter(row => Number.isInteger(row?.dayOfWeek) && days[row.dayOfWeek] && row.isClosed !== true && time(row.openTime) && time(row.closeTime) && row.openTime.slice(0, 5) !== row.closeTime.slice(0, 5)).sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.openTime.localeCompare(b.openTime)).map(row => ({ '@type': 'OpeningHoursSpecification', dayOfWeek: `https://schema.org/${days[row.dayOfWeek]}`, opens: row.openTime, closes: row.closeTime }));
  return { sameAs: sameAs.length ? sameAs : undefined, hours: hours.length ? hours : undefined };
}
const coordinate = (value, maximum) => {
  if (value == null || (typeof value !== 'number' && typeof value !== 'string') || (typeof value === 'string' && !value.trim())) return undefined;
  const number = Number(value);
  return Number.isFinite(number) && Math.abs(number) <= maximum ? number : undefined;
};

export function publicBusinessLocationFacts(listing = {}) {
  const physicalPublic = listing.locationType === 'PHYSICAL' && listing.addressVisibility === 'PUBLIC';
  const parts = physicalPublic ? {
    street: [text(listing.addressLine1), text(listing.addressLine2)].filter(Boolean).join(', '),
    city: text(listing.city), region: text(listing.state), postalCode: text(listing.postalCode),
    country: text(listing.addressCountry || listing.country),
  } : null;
  const latitude = physicalPublic ? coordinate(listing.lat, 90) : undefined;
  const longitude = physicalPublic ? coordinate(listing.lng, 180) : undefined;
  const areaServed = [...new Set((Array.isArray(listing.serviceAreas) ? listing.serviceAreas : []).map(area =>
    text(area?.label) || [text(area?.city), text(area?.state), text(area?.postalCode)].filter(Boolean).join(', ')
  ).filter(Boolean))];
  return {
    address: parts && Object.values(parts).some(Boolean) ? parts : undefined,
    geo: latitude !== undefined && longitude !== undefined ? { latitude, longitude } : undefined,
    areaServed: areaServed.length ? areaServed : undefined,
  };
}
